import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TrackingEvent } from '@models/trackingEvents/trackingEvent.model';

import { StorageService } from '@services/Logic/storage.service';

import { environment } from '../../../environments/environment';
import { md5 } from '@app/utils/encode';

@Injectable()
export class EventService {
  budgetSuggestionEvents: string[] = [
    'budget_suggestion_enabled',
    'budget_suggestion_disabled',
    'budget_suggestion_generated',
    'budget_suggestion_applied',
    'reached_this_step',
  ];

  constructor(
    private route: Router,
    private storageService: StorageService,
  ) {}


  /**
   * AdTraction implementation @link https://adtraction.com/jstagcookie.htm?tp=1909602835
   * @param orderId - unique order reference - in our case the task id or offer id
   * @param user
   */
  adTractionAffiliateEvent(orderId, user) {
    if (environment.production) {
      if ('ADT' in window) {
        const { ADT }: any = window;
        ADT.Tag.t = 4;
        ADT.Tag.c = 'DKK';
        ADT.Tag.tp = 1909602835;
        ADT.Tag.ti = `${orderId}`;
        ADT.Tag.xd = md5(user.email);
        ADT.Tag.doEvent();
      } else {
        console.warn('ADT is not defined');
      }
    }
  }

  private pushToDataLayer(event: any): void {
    if ('dataLayer' in window) {
      window.dataLayer.push(event);
    } else {
      console.warn('window.dataLayer is not defined');
    }
  }

  generateAnalyticsUserData(userData: any | null) {
    if (!userData) {
      return {};
    }
    return {
      enhanced_conversion_data: {
        email: userData?.email,
        phone_number: userData?.phone ? `+45${userData.phone}` : undefined,
        address: {
          first_name: userData?.firstName ? userData.firstName : undefined,
          last_name: userData?.lastName ? userData.lastName : undefined,
          city: userData?.city ? userData.city : undefined,
          postal_code: userData?.zipCode ? userData.zipCode : undefined,
          country: 'Denmark',
        },
      },
      enhanced_matching_data: {
        email: userData?.email.toLowerCase(),
        phone_number: userData?.phone ? `0045${userData.phone}` : undefined,
        address: {
          first_name: userData?.firstName
            ? userData.firstName?.toLowerCase()
            : undefined,
          last_name: userData?.lastName
            ? userData.lastName?.toLowerCase()
            : undefined,
          city: userData?.city
            ? userData.city?.toLowerCase()?.replace(/\s/g, '')
            : undefined,
          postal_code: userData?.zipCode
            ? userData?.zipCode?.toLowerCase()
            : undefined,
          country: 'dk',
        },
      },
    };
  }

  /* Google Event - sign_up */
  customEventSignUp(user?, method?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'sign_up_datalayer',
        value: 1,
        method: method ?? '',
        userId: user?.id ?? '',
        transaction_id: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventViewTask(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'view_task_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  /* Google Event - create_offer */
  customEventCreateOffer(offerId: number, user?, taskId?, priceBid?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'create_offer_datalayer',
        taskId: taskId ?? '',
        value: 20,
        create_offer_value: priceBid ?? '',
        userId: user?.id ?? '',
        transaction_id: `${offerId}`,
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventCompleteTask(user, task, priceBid) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'complete_task_datalayer',
        taskId: task?.id ?? '',
        taskValue: priceBid ?? '',
        userId: user?.id ?? '',
        transaction_id: `${user?.id ?? ''}${task?.id ?? ''}`,
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventRequestHandyhandNumber(user?, task?, priceBid?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'request_phone_from_handyhand_datalayer',
        taskId: task?.id ?? '',
        taskValue: priceBid ?? '',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventRequestTaskOwnerNumber(user?, task?, priceBid?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'request_phone_from_task_owner_datalayer',
        taskId: task?.id ?? '',
        taskValue: priceBid ?? '',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventSendMessage(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'send_message_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customClickOutToAppStore(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'click_out_to_app_store_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventSendComment(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'send_comment_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventSendReview(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'send_review_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  customEventRequestCancellation(user?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'send_request_cancellation_datalayer',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  hasPayments(payments: number): boolean {
    return payments < 1;
  }

  /* Google Event - assign_task_with_value */
  customEventAssignTask(offer?, user?) {
    if (environment.production) {
      const newUser: boolean = user
        ? this.hasPayments(user?.numberOfPayments[0]?.count)
        : false;
      const totalFees = offer ? offer.fee + offer.taskOwnerFee / 100 : 0;
      // remove the moms from the fee
      const totalFeesNoMoms = totalFees * 0.8;
      const gtmTag = this.filterEmptyValues({
        event: 'assign_task_with_value',
        ecommerce: {
          currencyCode: environment.currency,
          transaction_id: offer?.id ?? '', // Replace with a unique transaction ID
          value: totalFeesNoMoms, // Total order value
          currency: environment.currency,
          items: [
            {
              item_id: offer?.taskId ?? '', // offer ID
              item_name: 'Offer accepted', // name or category
              currency: environment.currency,
              price: totalFeesNoMoms, // price of the offer
              task_id: offer?.taskId ?? '', // task associated with the offer
            },
          ],
        },
        value: totalFeesNoMoms,
        offerIdOnAssign: offer?.id ?? '',
        currency: environment.currency,
        userId: user?.id ?? '',
        new_customer: newUser,
        transaction_id: offer?.id ?? '', // Replace with a unique transaction ID
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
      if (newUser) {
        this.customEventAssignTaskPlusCLV(offer, user);
      }
    }
  }

  /* Google Event - assign_task_with_value + an estimate for CLV ( 75 kr. at the moment) */
  customEventAssignTaskPlusCLV(offer?, user?) {
    if (environment.production) {
      const newUser: boolean = user
        ? this.hasPayments(user?.numberOfPayments[0]?.count)
        : false;
      const totalFees = offer ? offer.fee + offer.taskOwnerFee / 100 + 75 : 0;
      // remove the moms from the fee
      const totalFeesNoMoms = totalFees * 0.8;
      const gtmTag = this.filterEmptyValues({
        event: 'assign_task_with_value_plus_clv',
        ecommerce: {
          currencyCode: environment.currency,
          transaction_id: offer?.id ?? '', // Replace with a unique transaction ID
          value: totalFeesNoMoms, // Total order value
          currency: environment.currency,
          items: [
            {
              item_id: offer?.taskId ?? '', // offer ID
              item_name: 'Offer accepted', // name or category
              currency: environment.currency,
              price: totalFeesNoMoms, // price of the offer
              task_id: offer?.taskId ?? '', // task associated with the offer
            },
          ],
        },
        value: totalFeesNoMoms,
        offerIdOnAssign: offer?.id ?? '',
        currency: environment.currency,
        userId: user?.id ?? '',
        new_customer: newUser,
        transaction_id: offer?.id ?? '', // Replace with a unique transaction ID
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  /* Google Event - create_task */
  customEventCreateTask(user?, task?, taskId?) {
    if (environment.production) {
      const conversionValue = task
        ? this.calculateConversionValue(task.budget)
        : 0;
      const gtmTag = this.filterEmptyValues({
        event: 'create_task_datalayer',
        taskId: taskId ?? '',
        value: conversionValue,
        task_budget: task?.budget ?? '',
        new_user: user?.tasksCreated === 0,
        userId: user?.id ?? '',
        transaction_id: taskId ?? task?.id,
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }

    // AdTraction affiliate event
    this.adTractionAffiliateEvent(taskId, user);
  }

  // push GoogleTagManager data layer with a custom event
  customEventSearchTasks(user?, searchQuery?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'search_task_datalayer',
        search: searchQuery ?? '',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  // push GoogleTagManager data layer with a custom event
  // eslint-disable-next-line camelcase
  customEventSelectsTask(user?, content_type?, content_id?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'select_content_datalayer',
        // eslint-disable-next-line camelcase
        content_type: content_type ?? '',
        // eslint-disable-next-line camelcase
        content_id: content_id ?? '',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  // A tag to ensure that datalayer is encriched on pageviews
  customNewPageEvent(user?, pageName?) {
    if (environment.production) {
      const gtmTag = this.filterEmptyValues({
        event: 'page',
        userId: user?.id ?? '',
        gbuuid: this.storageService.getGrowthbookUUID(),
        pageName: pageName ?? '',
        ...this.generateAnalyticsUserData(user),
      });
      this.pushToDataLayer(gtmTag);
    }
  }

  calculateConversionValue(budget: number) {
    let assignmentRate: number;
    let commission: number;

    if (budget < 750) {
      assignmentRate = 0.281;
      commission = 0.1659;
    } else if (budget < 1500) {
      assignmentRate = 0.3077;
      commission = 0.1596;
    } else if (budget < 2250) {
      assignmentRate = 0.3031;
      commission = 0.1599;
    } else if (budget < 3000) {
      assignmentRate = 0.2637;
      commission = 0.1655;
    } else if (budget < 3750) {
      assignmentRate = 0.2463;
      commission = 0.1637;
    } else if (budget < 4500) {
      assignmentRate = 0.2661;
      commission = 0.1313;
    } else if (budget < 5000) {
      assignmentRate = 0.166;
      commission = 0.1323;
    } else if (budget < 10000) {
      assignmentRate = 0.1564;
      commission = 0.1319;
    } else if (budget < 15000) {
      assignmentRate = 0.1157;
      commission = 0.1343;
    } else if (budget < 20000) {
      assignmentRate = 0.1105;
      commission = 0.1335;
    } else if (budget < 25000) {
      assignmentRate = 0.0636;
      commission = 0.1233;
    } else if (budget < 30000) {
      assignmentRate = 0.0667;
      commission = 0.1161;
    } else {
      assignmentRate = 0.0408;
      commission = 0.1022;
    }
    return assignmentRate * (budget * commission);
  }

  /* ServerSide Tracking on API - Google Ads Conversion API + FB conversion API */
  initializeServerSideTrackingEvents() {
    let adsClick = Array.isArray(this.storageService.adsClickObj)
      ? this.storageService.adsClickObj.at(-1)
      : this.storageService.adsClickObj;
    if (!adsClick) {
      adsClick = this.getTrackingConversionFromCookie();
    }
    const _fbpCookie = this.storageService.getCookie('_fbp');
    const _fbcCookie = this.storageService.getCookie('_fbc');
    const serversideTrackingEvent: TrackingEvent = {
      adsClickType: adsClick?.adsClickType,
      adsClickValue: adsClick?.adsClickValue,
      clickTimeStamp: adsClick?.clickTimeStamp,
      fbTracking: {
        userAgent: navigator.userAgent,
        _fbp: _fbpCookie,
        _fbc: _fbcCookie,
        eventSourceUrl: `${environment.domain}${this.route.url}`,
      },
    };
    return serversideTrackingEvent;
  }

  getTrackingConversionFromCookie(): any {
    const adClickIds: string[] = [
      'gclid',
      'wbraid',
      'gbraid',
      'msclkid',
      'fbclid',
      'SsCid',
      'TTCLID',
      'twclid',
    ];
    const foundId = adClickIds.find((id) => this.storageService.getCookie(id));
    if (foundId) {
      return {
        adsClickType: foundId,
        adsClickValue: this.storageService.getCookie(foundId),
        clickTimeStamp: this.storageService.getCookie('clickTimeStamp'),
      };
    }
    return undefined;
  }

  /* Google Event - Budget suggestion */
  customEventBudgetSuggestion(
    eventName: string,
    value: number,
    extraObjData = null,
  ) {
    if (
      this.budgetSuggestionEvents.includes(eventName) &&
      environment.production
    ) {
      const pushObj = {
        event: eventName,
        value,
        gbuuid: this.storageService.getGrowthbookUUID(),
      };
      if (extraObjData) {
        Object.assign(pushObj, extraObjData);
      }
      this.pushToDataLayer(pushObj);
    }
  }

  customEventGrowthbookExperiment(trackingCallbackData, user) {
    if (environment.production && environment.env === 'production') {
      this.pushToDataLayer({
        ...trackingCallbackData,
        ...this.generateAnalyticsUserData(user),
      });
    }
  }

  filterEmptyValues(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value != null && value !== ''),
    );
  }

  justPushToDataLayer(obj: any) {
    if (environment.production) {
      this.pushToDataLayer(obj);
    }
  }
}
